import { Injectable } from '@angular/core';
import {
    InputOutputType,
    ModuleDataType,
    ModulePropertySpecification,
    ModuleRequirements,
    ModuleSpecification,
    SpecificationPropertyObject,
    SpecificationSection,
    SpecProperty,
    UIProperty,
} from '@ca/core/models';
import clone from 'clone-deep';
import deepEqual from 'deep-equal';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { getSpecificationPropertyObject } from '../../../../utils/utils';

@Injectable()
export class ModuleSpecService {
    private readonly moduleDataTypePrefix = 'type:';
    private _activeProperty: SpecificationPropertyObject | undefined;
    private activePropChanged$$: Subject<SpecificationPropertyObject> = new Subject();
    private specification$$: BehaviorSubject<ModuleSpecification | null> = new BehaviorSubject(null);

    get activePropertyChanged$(): Observable<SpecificationPropertyObject> {
        return this.activePropChanged$$.asObservable();
    }

    get specification$(): Observable<ModuleSpecification | null> {
        return this.specification$$.asObservable();
    }

    setSpecification(spec: ModuleSpecification) {
        this.specification$$.next(spec);
    }

    /**
     * Updates the label (and the key) of the ActiveUiProp
     */
    updateLabel(newLabel: string) {
        if (this._activeProperty != null && this._activeProperty.uiProperty != null) {
            const activeProp: SpecificationPropertyObject = clone(this._activeProperty);
            activeProp.uiProperty!.label = newLabel;

            // Remove the old Input/Output
            this.removeInputOutput(this._activeProperty.uiProperty, this._activeProperty.type);

            // Add new Property (spec and UI)
            const added: boolean = this.addInputOutput(this._activeProperty.type, this._activeProperty.uiProperty, this._activeProperty.specProperty);

            if (added === true) {
                this.setActiveProp(this._activeProperty.uiProperty);
            }
        }
    }

    getActiveProperty(): SpecificationPropertyObject | undefined {
        return this._activeProperty;
    }

    isPropertyActive(property: UIProperty): boolean {
        let isActive: boolean = false;
        if (this._activeProperty != null && this.getPropertyKey(property) === this._activeProperty.key) {
            isActive = true;
        }
        return isActive;
    }

    /**
     * Adds an Input/Output property to the spec
     *
     * @param uiProperty if provided, adds this UI property to the spec.
     *                   If undefined, it creates a new one (with default name, key etc).
     *
     * @returns boolean - true if correctly added, otherwise false
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addInputOutput(type: InputOutputType, uiProperty?: UIProperty, specProp?: SpecProperty<any>, selectNewlyAddedProp: boolean = false): boolean {
        let propertyAdded: boolean = false;
        const specValue = this.specification$$.getValue();

        if (specValue != null) {
            const specificationDeepCopy = JSON.parse(JSON.stringify(specValue));

            if (uiProperty == undefined) {
                const index: number = this.generateNewPropertyIndex(type);
                const label: string = this.generateNewPropertyName(type, index);

                uiProperty = {
                    index,
                    label,
                };
            }

            const key: string = this.generateKeyByLabel(uiProperty.label);

            if (specProp == undefined) {
                specProp = {};

                // Default for Inputs is Number
                let moduleTypeKey: string = this.getModuleTypeKey('number');

                // For Outputs we put File as default
                if (type === InputOutputType.Output) {
                    moduleTypeKey = this.getModuleTypeKey('file');
                }
                specProp[moduleTypeKey] = {} as ModulePropertySpecification;
            }

            if (specificationDeepCopy.ui == null) {
                specificationDeepCopy.ui = new SpecificationSection<UIProperty>();
            }

            if (specificationDeepCopy.spec == null) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                specificationDeepCopy.spec = new SpecificationSection<SpecProperty<any>>();
            }

            if (type === InputOutputType.Input) {
                specificationDeepCopy.ui.inputs[key] = uiProperty;
                specificationDeepCopy.spec.inputs[key] = specProp;
            } else {
                specificationDeepCopy.ui.outputs[key] = uiProperty;
                specificationDeepCopy.spec.outputs[key] = specProp;
            }

            propertyAdded = true;

            if (propertyAdded === true && selectNewlyAddedProp === true) {
                this.setActiveProp(uiProperty);
            }
            this.specification$$.next(specificationDeepCopy);
        }

        return propertyAdded;
    }

    /**
     * Removes the input or output parameter from the specification
     * This method removes the spec AND the ui part (if available)
     */
    removeInputOutput(parameter: UIProperty, type: InputOutputType, resetActiveProp: boolean = false) {
        const specValue = this.specification$$.getValue();

        if (specValue != null) {
            const specificationDeepCopy = JSON.parse(JSON.stringify(specValue));

            const key: string | undefined = this.getPropertyKey(parameter);

            if (key != null) {
                if (type === InputOutputType.Input && specificationDeepCopy.spec.inputs[key] != null) {
                    // Remove spec part
                    delete specificationDeepCopy.spec.inputs[key];

                    // Remove UI part (if exists)
                    if (specificationDeepCopy.ui != null && specificationDeepCopy.ui.inputs[key] != null) {
                        delete specificationDeepCopy.ui.inputs[key];
                    }
                } else if (type === InputOutputType.Output && specificationDeepCopy.spec.outputs[key] != null) {
                    // Remove spec part
                    delete specificationDeepCopy.spec.outputs[key];

                    // Remove UI part (if exists)
                    if (specificationDeepCopy.ui != null && specificationDeepCopy.ui.outputs[key] != null) {
                        delete specificationDeepCopy.ui.outputs[key];
                    }
                }
            }

            if (resetActiveProp === true) {
                this.resetActiveProp();
            }

            this.specification$$.next(specificationDeepCopy);
        }
    }

    getUiProperties(inputOutput: InputOutputType): UIProperty[] {
        const specValue = this.specification$$.getValue();

        let props: UIProperty[] = [];

        if (specValue != null && specValue.ui != null) {
            if (inputOutput === InputOutputType.Input && specValue.ui.inputs != null) {
                props = Object.values(specValue.ui.inputs);
            } else if (inputOutput === InputOutputType.Output && specValue.ui.outputs != null) {
                props = Object.values(specValue.ui.outputs);
            }
        }

        return props.sort((a: UIProperty, b: UIProperty) => a.index - b.index);
    }

    setActiveProp(prop: UIProperty | undefined) {
        const specValue = this.specification$$.getValue();

        if (specValue != null) {
            let activePropKey: string | undefined = undefined;

            // Find the property key
            if (prop != null) {
                activePropKey = this.getPropertyKey(prop);

                if (activePropKey != null) {
                    const activeProp: SpecificationPropertyObject | undefined = getSpecificationPropertyObject(activePropKey, specValue);

                    if (activeProp != null) {
                        this._activeProperty = activeProp;

                        // Fire event notifying that a new active property was set
                        this.activePropChanged$$.next(activeProp);
                    }
                }
            }
        }
    }

    resetActiveProp(): void {
        this._activeProperty = undefined;
        // @ts-ignore
        this.activePropChanged$$.next(this._activeProperty);
    }
    getGpuUsage(): boolean {
        let isGpuSet: boolean = false;
        const requirements: ModuleRequirements | undefined = this.specification$$.getValue()?.requirements;
        if (requirements != null && requirements.hardware != null) {
            isGpuSet = requirements && requirements.hardware && !!requirements.hardware.gpu;
        }

        return isGpuSet;
    }
    setGpuUsage(val: boolean) {
        const specValue = this.specification$$.getValue();

        if (specValue != null) {
            const specificationDeepCopy = JSON.parse(JSON.stringify(specValue));

            const requirements: ModuleRequirements | undefined =
                specificationDeepCopy.requirements || (specificationDeepCopy.requirements = new ModuleRequirements());

            if (val && requirements) {
                requirements.hardware = { gpu: {} };
            } else {
                if (requirements?.hardware) {
                    delete requirements.hardware.gpu;
                    delete requirements.hardware;
                }
                if (!requirements?.network) {
                    delete specificationDeepCopy.requirements;
                }
            }

            this.specification$$.next(specificationDeepCopy);
        }
    }

    getInternetAccess(): boolean {
        let isInternetAccessSet: boolean = false;
        const requirements: ModuleRequirements | undefined = this.specification$$.getValue()?.requirements;
        if (requirements != null && requirements.network != null) {
            isInternetAccessSet = requirements && requirements.network && !!requirements.network.internet_access;
        }

        return isInternetAccessSet;
    }
    setInternetAccess(val: boolean) {
        const specValue = this.specification$$.getValue();

        if (specValue != null) {
            const specificationDeepCopy = JSON.parse(JSON.stringify(specValue));

            const requirements: ModuleRequirements | undefined =
                specificationDeepCopy.requirements || (specificationDeepCopy.requirements = new ModuleRequirements());

            if (val && requirements) {
                requirements.network = { internet_access: {} };
            } else {
                if (requirements?.network) {
                    delete requirements.network.internet_access;
                    delete requirements.network;
                }
                if (!requirements?.hardware) {
                    delete specificationDeepCopy.requirements;
                }
            }

            this.specification$$.next(specificationDeepCopy);
        }
    }

    /**
     * Finds the property key in the specification by iterating through the
     * spec or UI part and comparing the value object
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getPropertyKey(property: UIProperty | SpecProperty<any>): string | undefined {
        let propKey: string | undefined = undefined;

        let specOrUi: 'spec' | 'ui' = 'spec';
        // If label and index is defined, we assume that it's a UIProperty
        // (we cannot use instanceof since type checking doesn't work for interfaces)
        if (property.label != null && property.index != null) {
            specOrUi = 'ui';
        }

        const specification = this.specification$$.getValue();

        // we need a null check here, since the ui part can be null
        if (specification && specification[specOrUi] != null) {
            // Check for the given prop in input parameters
            for (const [key, value] of Object.entries(specification[specOrUi]!.inputs)) {
                if (deepEqual(value, property)) {
                    propKey = key;
                    break;
                }
            }

            if (specification[specOrUi]!.outputs != null) {
                // Check for the given prop in output parameters
                for (const [key, value] of Object.entries(specification[specOrUi]!.outputs)) {
                    if (deepEqual(value, property)) {
                        propKey = key;
                        break;
                    }
                }
            }
        }

        return propKey;
    }

    private getModuleTypeKey(type: ModuleDataType): string {
        const key = this.moduleDataTypePrefix + type;

        return key;
    }

    private keyExistsInSpecification(key: string): boolean {
        let exists: boolean = false;

        if (this.specification$$.getValue()?.spec?.inputs?.hasOwnProperty(key) || this.specification$$.getValue()?.spec?.outputs.hasOwnProperty(key)) {
            exists = true;
        }

        return exists;
    }

    private generateKeyByLabel(label: string): string {
        let key: string = label.trim().replace(/ /g, '_');
        key = key.toLowerCase();

        if (this.keyExistsInSpecification(key)) {
            key = key + '_' + Math.floor(Math.random() * 1000);

            // Request from BF: Safety check in case the key exists again
            if (this.keyExistsInSpecification(key)) {
                key = key + '_' + Math.floor(Math.random() * 1000);
            }
        }

        return key;
    }

    private generateNewPropertyName(type: InputOutputType, newindex: number): string {
        return type + ' ' + newindex;
    }

    private generateNewPropertyIndex(inputOutput: InputOutputType): number {
        const props: UIProperty[] = this.getUiProperties(inputOutput);

        let maxIndex: number = 1;
        if (props.length > 0) {
            maxIndex = Math.max(...props.map(input => input.index)) + 1;
        }

        return maxIndex;
    }
}
